import { useReadContract, useReadContracts } from 'wagmi'
import { T } from '@/lib/theme'
import { SectionHeader } from '@/components/dashboard/SectionHeader'
import { Panel, SectionLabel, Stat, Row } from '@/components/ui'
import { ADDRESSES, IS_LIVE, addressLink, ZERO_ADDR } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import { fmt, fmtUsd, fmtPct, fmtUsdCompact, truncAddr, POLL_SLOW, fmtBps } from '@/lib/utils'
import { useProtocolState } from '@/hooks/useProtocolState'
import { EmptyState } from '@/components/dashboard/EmptyState'
import { useVaultsList } from '@/hooks/useVaultsList'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { StatsMarketPanel } from '@/components/dashboard/StatsMarketPanel'
import { LiquidationsSection } from '@/components/dashboard/LiquidationsSection'


/// The Stats page (was "Stats", briefly "DAO", now "Stats" again - "DAO" implied a governance/voting
/// surface that does not exist here). A mostly read-only protocol overview, an at-a-glance Overview
/// down to progressively more detail, rather than a flat wall of same-weight cards:
///   1. Overview   - the handful of numbers that answer "how big, is it healthy, is it earning"
///   2. Markets    - each reserve's own activity (deposits/borrows/rates/utilization)
///   3. Protocol Health - the risk/reserve levers themselves, per reserve.
/// Then Liquidations - the one real action anyone can take on this page - rendered directly rather
/// than under its own numbered section, since it already carries its own SectionHeader (formerly a
/// separate `/liquidations` page, merged in here; old links redirect - see App.jsx). Buyback used
/// to sit above it as a second action; it split back out to its own `/buyback` page (founder call)
/// since the daily open-market repurchase is a standing mechanism, not a footnote to the stats.
/// No History panel above Liquidations any more - removed outright, not folded elsewhere.
/// HORIZON supply/price/market cap/staking figures live on their own pages (Portfolio, Stake)
/// rather than duplicated here.
///
/// No "Distribute Reserves"/"Distribute Fees" panels anymore - both called permissionless,
/// bounty-paying crank functions (`HorizonLendingPool.sweepReserves`, `HorizonFarm.harvestAllFees`)
/// that don't need a public UI to run: `scripts/keeper.cjs`, triggered from the team's own
/// wallet on a schedule, calls both directly now. A public button inviting any connected wallet
/// to spend gas competing for a 3% bounty added surface area this page doesn't need to carry.
///
/// No more "ETH Market (Market B)" panel or "Revenue split" distribution bar - both were
/// specific to the retired two-market design (a second market that borrowed the first market's
/// own pooled ETH, and its bountied 3-way sweepReserves split). `HorizonLendingPool` has one
/// reserve list instead of two markets (StatsMarketPanel below shows every reserve's own row).
///
/// Implementation-detail numbers (treasury address, unswept reserves) live behind each panel's
/// own "Contract details" disclosure rather than sitting at the same visual weight as the
/// headline figures - closed by default.
/// Protocol TVL sums every contract that actually holds value, not just the money market:
/// - Money market (below): each reserve's totalSupplied, priced through its own feed.
/// - Vaults: every active ALM vault's total liquidity, from useVaultsList - the same TWAP-derived
///   pricing the Vaults page itself shows, so the two surfaces always agree.
/// - Staking: HorizonVoteEscrow's own raw HORIZON/USDx/ETH balances - reading the contract's real
///   balance directly (rather than reconstructing from its internal accounting) naturally covers
///   staked principal + pending HORIZON rewards + unclaimed multi-asset revenue share all at once.
/// - Treasury: computed below, already its own card.
/// - Buyback: the HorizonBuybackArray's WETH balance (its ETH lane, where the RevenueRouter's 64% leg
///   + mint deposits land between permissionless lane cranks); the TITANX/X28 lanes hold in-flight
///   value not counted here, and bought-back HORIZON is burned or forwarded to Treasury per the split.
/// RevenueRouter deliberately excluded - "a stateless pass-through... never leaves a balance
/// sitting here across transactions" per its own doc comment, so there's genuinely nothing to
/// read. HToken contracts excluded too - pure accounting receipts, the real assets they represent
/// already live in HorizonLendingPool and are counted via totalSupplied (reading HToken balances
/// too would double-count).
export function StatsSection() {
  const { d, deployed, isLoading } = useProtocolState()
  const eth = d.reserves.find((r) => r.key === 'eth')
  const { rate: horizonPriceUsd, found: hasHorizonPrice } = useHorizonPriceUsd()

  const hasTreasury = d.treasury && d.treasury !== ZERO_ADDR
  const { data: treasuryReads } = useReadContracts({
    contracts: [
      { address: ADDRESSES.USDX, abi: ERC20_ABI, functionName: 'balanceOf', args: [d.treasury ?? ZERO_ADDR] },
      { address: ADDRESSES.HORIZON_TOKEN, abi: ERC20_ABI, functionName: 'balanceOf', args: [d.treasury ?? ZERO_ADDR] },
      { address: ADDRESSES.WETH, abi: ERC20_ABI, functionName: 'balanceOf', args: [d.treasury ?? ZERO_ADDR] },
    ],
    query: { enabled: IS_LIVE && hasTreasury, refetchInterval: POLL_SLOW },
  })
  // Liquidation fee payouts and reserve sweeps land as WETH now, not native ETH - the pool has
  // no native-ETH concept at all anymore (see HorizonLendingPool.sol's own doc comment), so this
  // is a plain ERC20 balanceOf, folded into the multicall above instead of a separate useBalance
  // native-ETH read.
  const treasuryUsdx = treasuryReads?.[0]?.result
  const treasuryHorizon = treasuryReads?.[1]?.result
  const treasuryWeth = treasuryReads?.[2]?.result
  const treasuryUsdxNum = treasuryUsdx != null ? Number(treasuryUsdx) / 1e18 : 0
  const treasuryHorizonNum = treasuryHorizon != null ? Number(treasuryHorizon) / 1e18 : 0
  const treasuryEthNum = treasuryWeth != null ? Number(treasuryWeth) / 1e18 : 0
  const treasuryEthUsd = treasuryEthNum * (eth?.priceUsd ?? 0)
  const treasuryHorizonUsd = treasuryHorizonNum * horizonPriceUsd
  const treasuryTotalUsd = treasuryUsdxNum + treasuryEthUsd + treasuryHorizonUsd

  // Money-market TVL/Borrowed - summed across every reserve, each priced through its own feed.
  const moneyMarketTvlUsd = d.reserves.reduce((sum, r) => sum + r.totalSupplied * r.priceUsd, 0)
  const totalBorrowedUsd = d.reserves.reduce((sum, r) => sum + r.totalBorrows * r.priceUsd, 0)

  // Vault TVL - every active ALM vault's total liquidity, from useVaultsList's own TWAP-derived
  // pricing (the same figures the Vaults page shows, so the two can never disagree). This line used
  // to sum LP farm shares; the farm is gone and its one pair is a vault now.
  const { vaults } = useVaultsList()
  const vaultTvlUsd = vaults.reduce((sum, v) => sum + (v.tvlUsd || 0), 0)

  // Locked TVL - HorizonVoteEscrow's own raw balances (locked principal + streaming revenue +
  // unclaimed multi-asset revenue share, all in one read - see the module doc comment above).
  // Revenue share can land here in WETH now (not native ETH - the pool has no native-ETH concept
  // at all anymore), so this is a plain ERC20 balanceOf like every other asset here.
  const { data: stakingReads } = useReadContracts({
    contracts: [
      { address: ADDRESSES.HORIZON_TOKEN, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_VOTE_ESCROW] },
      { address: ADDRESSES.USDX, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_VOTE_ESCROW] },
      { address: ADDRESSES.WETH, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_VOTE_ESCROW] },
    ],
    query: { enabled: IS_LIVE, refetchInterval: POLL_SLOW },
  })
  const stakingHorizonNum = stakingReads?.[0]?.result != null ? Number(stakingReads[0].result) / 1e18 : 0
  const stakingUsdxNum = stakingReads?.[1]?.result != null ? Number(stakingReads[1].result) / 1e18 : 0
  const stakingEthNum = stakingReads?.[2]?.result != null ? Number(stakingReads[2].result) / 1e18 : 0
  const stakingTvlUsd = stakingHorizonNum * horizonPriceUsd + stakingUsdxNum + stakingEthNum * (eth?.priceUsd ?? 0)

  // Buyback TVL - the buyback array's WETH balance (its ETH lane, where the router's 64% leg + mint
  // deposits land). Priced in USD via ETH; the TITANX/X28 lanes hold in-flight value not counted here.
  const { data: buybackWethRaw } = useReadContract({
    address: ADDRESSES.WETH, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_BUYBACK_ARRAY || ADDRESSES.BUYBACK],
    query: { enabled: IS_LIVE, refetchInterval: POLL_SLOW },
  })
  const buybackTvlUsd = buybackWethRaw != null ? (Number(buybackWethRaw) / 1e18) * (eth?.priceUsd ?? 0) : 0

  const protocolTvlUsd = moneyMarketTvlUsd + vaultTvlUsd + stakingTvlUsd + treasuryTotalUsd + buybackTvlUsd

  // Aggregate Health Ratio: raw (unweighted) collateral vs. debt in the MONEY MARKET specifically -
  // a quick systemic-risk signal for the lending/borrowing side, not a precise gate (individual
  // accounts already use liquidationThreshold-weighted collateral via healthFactor, and get
  // liquidated well before this aggregate figure would ever reach 100%). Deliberately uses
  // moneyMarketTvlUsd, not protocolTvlUsd - Farm LP/staked HORIZON/treasury/buyback balances are
  // real protocol value but aren't collateral backing any money-market debt, so folding them in
  // here would overstate this specific ratio's real meaning. null when there's no debt outstanding
  // yet - infinitely healthy, not a ratio to display as a number.
  const aggregateHealthRatioPct = totalBorrowedUsd > 0 ? (moneyMarketTvlUsd / totalBorrowedUsd) * 100 : null
  const aggregateHealthColor = aggregateHealthRatioPct == null ? T.positive
    : aggregateHealthRatioPct >= 150 ? T.positive
    : aggregateHealthRatioPct >= 110 ? T.warning
    : T.negative

  return (
    <div id="stats">
      <SectionHeader
        title="Stats"
        subtitle="Protocol health, treasury, and the liquidations anyone can help run."
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* ── 1. Overview ─────────────────────────────────────────────── */}
        <Panel style={{ padding: 32 }}>
          <SectionLabel>Overview</SectionLabel>
          <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24 }}>
            <div style={heroCell}><Stat size="lg" label="Protocol TVL" value={valueOrEmpty(protocolTvlUsd, fmtUsdCompact(protocolTvlUsd), 'Waiting for deposits')} /></div>
            <div style={heroCell}><Stat size="lg" label="Treasury" value={valueOrEmpty(treasuryTotalUsd, fmtUsdCompact(treasuryTotalUsd), 'Empty so far')} /></div>
            <div style={heroCell}>
              <Stat size="lg" label="Aggregate Health Ratio" color={aggregateHealthColor}
                value={aggregateHealthRatioPct == null ? 'No debt' : fmtPct(aggregateHealthRatioPct)}
                sub="Money market collateral vs. debt" />
            </div>
          </div>

          {/* Breakdown - Protocol TVL is a sum across five real value pools, not one contract's
              own number, so it's shown transparently rather than as an opaque headline figure. */}
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}`, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <Stat size="sm" label="Money market" value={fmtUsdCompact(moneyMarketTvlUsd)} />
            <Stat size="sm" label="Vaults" value={fmtUsdCompact(vaultTvlUsd)} />
            <Stat size="sm" label="Staking" value={fmtUsdCompact(stakingTvlUsd)} />
            <Stat size="sm" label="Treasury" value={fmtUsdCompact(treasuryTotalUsd)} />
            <Stat size="sm" label="Buyback" value={fmtUsdCompact(buybackTvlUsd)} />
          </div>
        </Panel>

        {/* ── 1b. HORIZON supply ────────────────────────────────────────── */}
        <HorizonSupplyPanel horizonPriceUsd={horizonPriceUsd} hasHorizonPrice={hasHorizonPrice} />

        {/* ── 2. Markets ───────────────────────────────────────────────── */}
        <div>
          <SectionLabel style={{ marginBottom: 14 }}>Markets</SectionLabel>
          {!deployed ? (
            <EmptyState
              title="No markets are live on this network yet."
              body="Listed reserves, their sizes, and their rates appear here as the money market goes live." />
          ) : (!isLoading && d.reserves.length === 0) ? (
            <EmptyState
              title="No markets listed yet."
              body="The money market is live. Each asset market appears here as soon as it is listed." />
          ) : (
            <StatsMarketPanel />
          )}
        </div>

        {/* ── 3. Protocol Health ──────────────────────────────────────── */}
        <div>
          <SectionLabel style={{ marginBottom: 14 }}>Protocol Health</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 28 }}>

            <Panel style={{ padding: 32 }}>
              <SectionLabel style={subLabelStyle}>Risk parameters</SectionLabel>
              <Row label="Close factor (global)" value={deployed ? fmtBps(d.closeFactorBps) : '—'} />
              {/* The live count of reserves actually listed on-chain, which is ZERO until the money
                  market is deployed - never the catalogue's length, which would claim markets exist
                  before genesis has added any. */}
              <Row label="Listed markets" value={deployed ? d.reserves.length : 0} />
            </Panel>

            <Panel style={{ padding: 32 }}>
              <SectionLabel style={subLabelStyle}>Treasury &amp; markets</SectionLabel>
              <Row label="Address" value={hasTreasury ? (
                <a href={addressLink(d.treasury)} target="_blank" rel="noreferrer" style={link} className="mono">{truncAddr(d.treasury)}</a>
              ) : '—'} />
              <Row label="USDx balance" value={fmt(treasuryUsdxNum, 2)} />
              <Row label="ETH balance" value={`${fmt(treasuryEthNum, 4)} (${fmtUsd(treasuryEthUsd)})`} />
              <Row label="HORIZON balance" value={`${fmt(treasuryHorizonNum, 2)} (${fmtUsd(treasuryHorizonUsd)})`} />
            </Panel>

          </div>

          {deployed && <PerMarketTable reserves={d.reserves} />}
        </div>

        {/* ── Liquidations - own SectionHeader inside, no outer label needed. Buyback used to sit
            above this; it split back out to its own /buyback page (App.jsx). ──── */}
        <LiquidationsSection />

      </div>
    </div>
  )
}

/// Every listed market's risk parameters and current state, one row each.
///
/// **Why a table rather than the stacked panels this replaced.** The old shape spent four rows per
/// market in "Risk parameters" and two more in "Treasury & markets", so each new listing added six
/// rows across two panels and the same four labels were repeated verbatim once per asset. At four
/// markets that was already 24 rows to read six numbers per asset; the listing set is meant to grow
/// (`useProtocolState` derives it from the pool's own registry, so genesis can add one with no
/// frontend change), and the stacked shape gets worse with every addition while a table gets
/// longer by exactly one row. It also makes the comparison the reader actually wants - "which
/// market has the loosest LTV" - a glance down a column instead of a hunt across panels.
///
/// The two panels keep what is genuinely singular: the global close factor, and the treasury's own
/// address and balances. Those are not per-market and would be noise as columns.
/// HORIZON supply at a glance: one donut over EVER-MINTED supply (current supply plus everything
/// burn-locked - burns leave circulation forever, and a chart that quietly dropped them would
/// understate the strongest commitment on the books). Four slices, every figure a live read:
///   liquid  = totalSupply - locked - LP   (whatever moves freely, treasury included)
///   locked  = HORIZON.balanceOf(ve)        (the decaying-lock principal the ve holds; the veNFT
///                                          rewrite dropped totalLocked, the held balance is it)
///   in LP   = HORIZON sitting in the HORIZON/WETH pool
///   burned  = HorizonVoteEscrow.totalBurnedHorizon()   (a direct getter now, no more halving power)
const TOTAL_SUPPLY_ABI = [{ type: 'function', name: 'totalSupply', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] }]
const VE_SUPPLY_ABI = [
  { type: 'function', name: 'totalBurnedHorizon', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint256' }] },
]

function HorizonSupplyPanel({ horizonPriceUsd, hasHorizonPrice }) {
  const { data: reads } = useReadContracts({
    contracts: [
      { address: ADDRESSES.HORIZON_TOKEN, abi: TOTAL_SUPPLY_ABI, functionName: 'totalSupply' },
      { address: ADDRESSES.HORIZON_TOKEN, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_VOTE_ESCROW ?? ZERO_ADDR] },
      { address: ADDRESSES.HORIZON_VOTE_ESCROW, abi: VE_SUPPLY_ABI, functionName: 'totalBurnedHorizon' },
      { address: ADDRESSES.HORIZON_TOKEN, abi: ERC20_ABI, functionName: 'balanceOf', args: [ADDRESSES.HORIZON_WETH_POOL ?? ZERO_ADDR] },
    ],
    query: { enabled: IS_LIVE, refetchInterval: POLL_SLOW },
  })
  const num = (i) => (reads?.[i]?.result != null ? Number(reads[i].result) / 1e18 : 0)
  const supply = num(0)
  const locked = num(1)
  const burned = num(2)
  const inLp = num(3)
  const liquid = Math.max(supply - locked - inLp, 0)
  // Ever-minted is the honest denominator; the max() guards a degenerate state where balances
  // outrun recorded supply (seen once on a dev chain after raw storage funding) so percentages
  // stay <= 100 instead of quietly lying.
  const everMinted = Math.max(supply + burned, liquid + locked + inLp + burned)

  const slices = [
    { label: 'Liquid', value: liquid, color: T.accent },
    { label: 'Locked', value: locked, color: T.positive },
    { label: 'In LP', value: inLp, color: T.cyan },
    { label: 'Burned', value: burned, color: T.burn },
  ]

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>HORIZON Supply</SectionLabel>
      <div style={{ marginTop: 18, display: 'flex', gap: 36, alignItems: 'center', flexWrap: 'wrap' }}>
        <SupplyDonut slices={slices} total={everMinted} centerLabel="Total supply" centerValue={fmt(supply, 0)} />
        <div style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {slices.map((s) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: s.color, flexShrink: 0, alignSelf: 'center' }} />
              <span style={{ color: T.text, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 600, width: 70 }}>{s.label}</span>
              <span className="mono" style={{ color: T.text, fontFamily: T.fontN, fontSize: T.fs.body }}>
                {fmt(s.value, 0)}
              </span>
              <span style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
                {everMinted > 0 ? `${((s.value / everMinted) * 100).toFixed(1)}%` : '0%'}
                {hasHorizonPrice && s.value > 0 ? ` · ${fmtUsdCompact(s.value * horizonPriceUsd)}` : ''}
              </span>
            </div>
          ))}
          <div style={{ marginTop: 6, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>
            Charted over every HORIZON ever minted. Burned HORIZON leaves supply forever and keeps 2x
            its amount in permanent governance power.
          </div>
        </div>
      </div>
    </Panel>
  )
}

/// A dependency-free SVG donut. Each slice is one circle stroke offset around the ring; a slice
/// under 0.25% of the total is dropped from the drawing (a sub-pixel arc reads as a rendering
/// glitch) while staying in the legend with its honest number.
function SupplyDonut({ slices, total, centerLabel, centerValue }) {
  const size = 190
  const stroke = 26
  const rad = (size - stroke) / 2
  const circ = 2 * Math.PI * rad
  let offset = 0
  const drawn = total > 0 ? slices.filter((s) => s.value / total >= 0.0025) : []
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="HORIZON supply breakdown">
      <circle cx={size / 2} cy={size / 2} r={rad} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} />
      {drawn.map((s) => {
        const frac = s.value / total
        const dash = frac * circ
        const el = (
          <circle
            key={s.label}
            cx={size / 2} cy={size / 2} r={rad} fill="none"
            stroke={s.color} strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        )
        offset += dash
        return el
      })}
      <text x="50%" y="46%" textAnchor="middle" fill={T.textDim} style={{ fontFamily: T.fontM, fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {centerLabel}
      </text>
      <text x="50%" y="58%" textAnchor="middle" fill={T.text} style={{ fontFamily: T.fontN, fontSize: 18, fontWeight: 700 }}>
        {centerValue}
      </text>
    </svg>
  )
}

function PerMarketTable({ reserves }) {
  const cols = [
    { key: 'symbol', label: 'Market', align: 'left', render: (r) => r.symbol },
    { key: 'ltv', label: 'Max LTV', render: (r) => fmtBps(r.collateralFactorBps) },
    { key: 'lt', label: 'Liq. threshold', render: (r) => fmtBps(r.liquidationThresholdBps) },
    { key: 'bonus', label: 'Liq. bonus', render: (r) => fmtBps(r.liquidationBonusBps) },
    { key: 'rf', label: 'Reserve factor', render: (r) => fmtBps(r.reserveFactorBps) },
    // A price of exactly 0 is never a real quote - it means `priceOf` reverted, which the old
    // layout rendered as "$0.00" and made indistinguishable from a live feed reporting nothing.
    // That cost a real debugging session: two markets sat at $0.00 for a day looking merely empty
    // while every supply, borrow and liquidation against them was reverting on a stale feed.
    {
      key: 'price',
      label: 'Price',
      render: (r) => (r.priceUsd > 0
        ? fmtUsd(r.priceUsd)
        : <span style={{ color: T.warning }} title="priceOf() is reverting - the feed is stale, unset or unreadable">unavailable</span>),
    },
    {
      key: 'unswept',
      label: 'Unswept reserves',
      render: (r) => (r.totalReservesAccrued > 0
        ? `${fmt(r.totalReservesAccrued, 6)} (${fmtUsd(r.totalReservesAccrued * r.priceUsd)})`
        : <span style={{ color: T.textDim }}>—</span>),
    },
  ]

  return (
    <div style={{ marginTop: 28 }}>
      <SectionLabel style={subLabelStyle}>Markets</SectionLabel>
      {/* The table owns its overflow so a narrow viewport scrolls THIS rather than the page. */}
      <Panel style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key} style={{ ...thStyle, textAlign: c.align ?? 'right' }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reserves.map((r, i) => (
              <tr key={r.key}>
                {cols.map((c) => (
                  <td
                    key={c.key}
                    className={c.key === 'symbol' ? undefined : 'mono'}
                    style={{
                      ...tdStyle,
                      borderBottom: i === reserves.length - 1 ? 'none' : tdStyle.borderBottom,
                      textAlign: c.align ?? 'right',
                      color: c.key === 'symbol' ? T.text : T.textMuted,
                      fontWeight: c.key === 'symbol' ? 600 : 400,
                    }}
                  >
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  )
}

const thStyle = {
  padding: '16px 20px', color: T.textDim, fontFamily: T.fontM, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap',
  borderBottom: `1px solid ${T.border}`,
}
const tdStyle = {
  padding: '14px 20px', fontFamily: T.fontM, fontSize: T.fs.body, whiteSpace: 'nowrap',
  borderBottom: `1px solid ${T.border}`,
}

/// Only the genuinely "does this look broken" zeros get a friendlier placeholder - a real zero
/// that's already good news (e.g. LiquidationsSection's own "0 Liquidatable now", shown in green)
/// stays a plain number; this is only for headline totals that read as "is this thing even live"
/// when blank.
function valueOrEmpty(numericValue, formatted, placeholder) {
  return numericValue > 0 ? formatted : <span style={{ color: T.textDim, fontWeight: 400 }}>{placeholder}</span>
}

// Grid items default to a content-based minimum width that ignores `1fr` sizing, so a wide
// unbroken string (a big comma-grouped number) can overflow its column instead of shrinking -
// `minWidth: 0` is the standard CSS Grid fix, applied everywhere a Stat sits directly in a grid.
const heroCell = { minWidth: 0 }

const subLabelStyle = { marginBottom: 8 }
const link = { color: T.accent, textDecoration: 'none' }
